/**
 * Odczyt zaproszenia po tokenie — jedyna operacja na zaproszeniach, którą front
 * wykonuje bezpośrednio przy Supabase.
 *
 * Wszystko, co zmienia stan zaproszenia (utworzenie, odrzucenie, anulowanie),
 * idzie przez `/api/invitations/*`, bo tam są limity: cooldown po odrzuceniu,
 * limit aktywnych zaproszeń i dławik wysyłki maili. Wcześniej ten moduł
 * dublował te operacje zapytaniami wprost do tabeli (INSERT/UPDATE/DELETE na
 * `family_invitations`), co te limity omijało — m.in. odrzucenie po samym
 * tokenie nie ustawiało `rejected_at`, więc 7-dniowy cooldown nie działał.
 * Żadna z tych funkcji nie miała już wywołań w aplikacji.
 */

import { supabase } from '../lib/supabase';
import type { FamilyRole } from './accessControl';

export interface Invitation {
  id: string;
  family_id: string;
  email: string;
  role: FamilyRole;
  token: string;
  /** Nie jest zwracane przez get_invitation_by_token — dostępne tylko przy odczycie tabeli przez członka rodziny. */
  invited_by?: string;
  created_at: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
}

/**
 * Pobiera zaproszenie na podstawie tokenu
 * @param token Token zaproszenia
 * @returns Zaproszenie lub null, jeśli nie znaleziono
 */
export const getInvitationByToken = async (token: string): Promise<Invitation | null> => {
  try {
    // Odczyt idzie przez funkcję SECURITY DEFINER, nie przez tabelę.
    //
    // Do 2026-08-10 było tu bezpośrednie `.from('family_invitations').select()`,
    // które działało dzięki polityce RLS `USING (true)` dla roli public. Ta sama
    // polityka pozwalała komukolwiek — bez konta, samym kluczem anon z bundla —
    // pobrać wszystkie zaproszenia razem z adresami e-mail i tokenami.
    // Polityka została usunięta; get_invitation_by_token zwraca wyłącznie
    // zaproszenie o dokładnie podanym tokenie, więc znajomość tokenu pozostaje
    // jedynym warunkiem dostępu i nie da się już wylistować cudzych zaproszeń.
    const { data, error } = await supabase
      .rpc('get_invitation_by_token', { p_token: token });

    if (error) {
      throw error;
    }

    const invitation = Array.isArray(data) ? data[0] : data;
    if (!invitation) {
      return null;
    }

    return invitation as Invitation;
  } catch (error) {
    console.error('❌ Błąd pobierania zaproszenia:', error);
    return null;
  }
};
